import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * E-Invoicing / Tax Compliance (PART 11, FIN-08) and the compliance strip it
 * feeds on the sales invoice (SAL-04 §5).
 *
 * PERMISSION AREA — verified against PART 16, not against FIN-08 §9.
 *
 *   PART 16.2.2's twelve-area table, **row 1**: "`sales` | Quotations, sales
 *   orders, delivery notes, sales invoices, returns, the point of sale,
 *   **e-invoicing**". Row 6 (`accounting`) reads "Chart of accounts, journal
 *   entries, vouchers, foreign exchange, fixed assets" and does not mention
 *   it. So the area is `sales`.
 *
 *   FIN-08 §9 says "reaching this screen requires the accounting-area
 *   permission", which contradicts row 1. PART 16 wins: §11.1.2 itself calls
 *   "the role model in PART 16 … the authority", and BR-PERM-36 requires
 *   every screen to be governed by an area, which row 1 assigns. Recorded as
 *   a spec inconsistency to report; the screen still LIVES in the Accounting
 *   & Finance menu group (FIN-08 §3), because where a menu entry sits and
 *   which permission guards it are different questions.
 *
 *   Per action, then:
 *     read the dashboard, the queue, the detail and the log  → `sales.view`
 *     Submit / Resubmit                                      → `sales.approve`
 *     send a cleared invoice to the customer                 → `sales.edit`
 *     change the Configuration tab                           → `admin.edit`
 *       (through /api/settings, whose §16.6 ladder already means
 *        "the Owner or admin.edit" — FIN-08 §9's "e-invoicing configuration
 *        permission, which the shipped Administrator role holds and the
 *        Owner always has", expressed inside the grid rather than as a
 *        private micro-permission BR-PERM-37 forbids.)
 *
 *   Submit is `sales.approve` because AP-10 in PART 16.5.2 is an approval
 *   flow, and because 16.5.2a settles the general question: one Approve
 *   action per area, never a private `einvoice.submit`.
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const EINVOICE_MESSAGES = {
    /** SAL-04 §5b banner 6 / ERR-SAL-04-01 — the strip's wording. */
    clearanceBlockedStrip: 'Clearance model — cannot be finalized or sent to the customer until the tax authority returns Accepted.',
    /** FIN-08 §5b — the compliance screen's wording of the same banner. */
    clearanceBlockedScreen: 'Clearance required — this invoice cannot be finalized or sent to the customer until the tax authority returns Accepted.',
    /** SAL-04 §5b banner 7. */
    clearedStrip: 'Cleared by the tax authority — valid to send.',
    /** FIN-08 §5b. */
    clearedScreen: 'Cleared by the tax authority — valid to send to the customer.',
    /** BR-FIN-08-05 — Submit exists only on a Pending record. */
    submitOnlyPending: 'This invoice has already been sent to the tax authority.',
    /** BR-FIN-08-05 — Resubmit exists only on a Rejected one. */
    resubmitOnlyRejected: 'Only a rejected invoice can be resubmitted.',
    /** §11 — the record is created when the invoice is ISSUED (Q-FIN-08-5). */
    notPosted: 'A draft invoice has nothing to file. Post it first.',
    /** §15 "An answer arriving twice" — a contradiction is raised, not applied. */
    contradictoryAnswer: 'The tax authority has already answered this invoice differently. Check the submission log.',
    /** FIN-08 §2 Prerequisites. */
    missingSellerTaxId: 'Enter the company tax registration number on the Configuration tab before submitting.',
    /** A jurisdiction the setting offers but nobody has implemented. */
    jurisdictionNotImplemented: (jurisdiction) => `The ${jurisdiction} e-invoicing adapter is not built yet. Choose the example jurisdiction, or wait for it.`,
    /** The document was already handed to the customer. */
    alreadySent: 'This invoice has already been sent to the customer.',
    /** §5c field 9's caption — shown under the QR tile, unchanged. */
    qrPlaceholderNote: 'Placeholder — encoding is jurisdiction-specific.',
};
// ── Shapes ──────────────────────────────────────────────────────────────────
/** FIN-08 §7 — four statuses, and no others. */
export const ComplianceStatusSchema = z.enum(['pending', 'submitted', 'accepted', 'rejected']);
/** §5b — the single field that changes the workflow. */
export const SubmissionModelSchema = z.enum(['clearance', 'reporting']);
/** §5d — three tax categories, and no others. */
export const TaxCategorySchema = z.enum(['standard', 'zero_rated', 'exempt']);
/** §5e — what an attempt was. */
export const AttemptActionSchema = z.enum(['submitted', 'resubmitted', 'callback']);
export const AttemptResultSchema = z.enum([
    'submitted',
    'accepted',
    'rejected',
    'error',
    /** §15 "An answer arriving twice": the repeat is logged and ignored. */
    'ignored',
]);
export const SubmissionSummarySchema = z
    .object({
    id: z.uuid(),
    documentType: z.enum(['invoice', 'credit_note']),
    /** BR-FIN-08-02's reference — INV-0042 / CN-0007. */
    documentCode: z.string(),
    invoiceId: z.uuid().nullable(),
    creditNoteId: z.uuid().nullable(),
    /** BR-FIN-08-12 — the two facts the prototype stored and never showed. */
    isCreditNote: z.boolean(),
    issuedAgainstCode: z.string().nullable(),
    /** §5c fields 2/3/4 — READ from the document, never copied onto the record. */
    documentDate: IsoDateSchema,
    buyerName: z.string(),
    total: z.string(),
    currency: z.string(),
    status: ComplianceStatusSchema,
    /** The jurisdiction USED — not whichever is selected today. */
    jurisdiction: z.string(),
    format: z.string(),
    submissionModel: SubmissionModelSchema,
    submittedAt: z.string().nullable(),
    /** §5c field 5 — the authority's own code, on acceptance. */
    authorityCode: z.string().nullable(),
    /** BR-FIN-08-09 — the authority's words and the field to fix, unchanged. */
    rejectionReason: z.string().nullable(),
    rejectedField: z.string().nullable(),
    /** "Recent submissions … with the authority's raw response truncated." */
    lastResponse: z.string().nullable(),
    sentToCustomerAt: z.string().nullable(),
})
    .strict();
/** §5d / BR-FIN-08-13 — per line: category, rate, net and tax. */
export const TaxBreakdownLineSchema = z
    .object({
    lineNo: z.number().int().positive(),
    description: z.string(),
    taxCategory: TaxCategorySchema,
    taxRate: z.string(),
    net: z.string(),
    tax: z.string(),
})
    .strict();
export const TaxBreakdownSchema = z
    .object({
    lines: TaxBreakdownLineSchema.array(),
    net: z.string(),
    tax: z.string(),
    /** The prototype computed gross and never displayed it. It is displayed. */
    gross: z.string(),
})
    .strict();
export const AttemptSchema = z
    .object({
    id: z.uuid(),
    attemptNo: z.number().int().positive(),
    action: AttemptActionSchema,
    result: AttemptResultSchema,
    occurredAt: z.string(),
    rawResponse: z.string(),
    rejectionReason: z.string().nullable(),
    rejectedField: z.string().nullable(),
    documentUuid: z.string().nullable(),
    /** §9 — every entry carries its actor. A callback has none. */
    actorName: z.string().nullable(),
})
    .strict();
export const SubmissionDetailSchema = SubmissionSummarySchema.extend({
    /**
     * The compliance block (§8's "Invoices detail"): the QR content, the
     * unique key, the signature and the generated file shown VERBATIM.
     *
     * When the record is accepted these are the frozen accepted columns —
     * what was filed, reproduced, never re-derived (DECISION.md §8.10). Before
     * acceptance they are the newest attempt's, so a Submitted invoice still
     * shows what went out; a Pending one has none of them yet and shows null.
     */
    uuid: z.string().nullable(),
    qrPayload: z.string().nullable(),
    signedPayload: z.string().nullable(),
    hash: z.string().nullable(),
    /** True once the accepted stamp exists — the database refuses changes then. */
    frozen: z.boolean(),
    taxBreakdown: TaxBreakdownSchema,
    /** §5e — newest first, for ever. */
    attempts: AttemptSchema.array(),
}).strict();
/** §8's Dashboard tab: the attention panel, four counts, recent submissions. */
export const ComplianceDashboardSchema = z
    .object({
    counts: z
        .object({
        pending: z.number().int().nonnegative(),
        submitted: z.number().int().nonnegative(),
        accepted: z.number().int().nonnegative(),
        rejected: z.number().int().nonnegative(),
    })
        .strict(),
    /**
     * "Needs attention" — appears only when something is rejected. Each row
     * carries the reason AND the offending field, because ruling R1 forbids
     * drawing Resubmit without the reason beside it.
     */
    attention: SubmissionSummarySchema.array(),
    recent: SubmissionSummarySchema.array(),
})
    .strict();
/** §8's Configuration tab, read side. Writes go through /api/settings. */
export const ComplianceConfigSchema = z
    .object({
    jurisdiction: z.string(),
    /** §5a "Name", and §4's banner label the prototype rendered empty. */
    jurisdictionName: z.string(),
    /** §4 — the example adapters are marked as examples. */
    isExample: z.boolean(),
    /** False for a country the setting offers but nobody has implemented. */
    implemented: z.boolean(),
    format: z.string(),
    submissionModel: SubmissionModelSchema,
    /** BR-FIN-08-10 — the label comes from the adapter. */
    taxIdLabel: z.string(),
    requiredFields: z.string().array(),
    /** The company's own number. Empty until somebody enters it. */
    taxId: z.string(),
    /** Whether THIS caller may change the tab (admin.edit or the Owner). */
    canConfigure: z.boolean(),
})
    .strict();
const submissionParams = z.object({ submissionId: z.uuid() }).strict();
const invoiceParams = z.object({ invoiceId: z.uuid() }).strict();
/**
 * The mock authority's answer, arriving after the fact (BR-FIN-08-14,
 * Q-FIN-08-1). A real authority pushes this to an unauthenticated signed
 * webhook or a poller fetches it — TODO(callback-transport) — but either
 * transport lands on this same handler, which is the point of having it.
 */
export const AuthorityAnswerRequestSchema = z
    .object({
    outcome: z.enum(['accepted', 'rejected']),
    /** Absent on an acceptance = the adapter derives it, as a portal would. */
    authorityCode: z.string().max(80).optional(),
    rejectionReason: z.string().max(500).optional(),
    rejectedField: z.string().max(120).optional(),
})
    .strict();
export const einvoicingContract = c.router({
    dashboard: {
        method: 'GET',
        path: '/api/einvoicing/dashboard',
        responses: {
            200: ComplianceDashboardSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'FIN-08 §8 Dashboard (sales.view): the four counts, the rejected attention panel with reason AND field, and recent submissions newest first.',
    },
    list: {
        method: 'GET',
        path: '/api/einvoicing/submissions',
        query: pagedQuery({ status: ComplianceStatusSchema.optional() }),
        responses: {
            200: pagedResponse('submissions', SubmissionSummarySchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The Invoices tab’s queue (sales.view), optionally filtered to one status — in SQL, and PAGED (NFR-42).',
    },
    get: {
        method: 'GET',
        path: '/api/einvoicing/submissions/:submissionId',
        pathParams: submissionParams,
        responses: {
            200: SubmissionDetailSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One compliance record (sales.view): the stored QR and signed file as submitted, the tax breakdown, and the append-only log.',
    },
    getByInvoice: {
        method: 'GET',
        path: '/api/einvoicing/invoices/:invoiceId',
        pathParams: invoiceParams,
        responses: {
            200: SubmissionDetailSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'What the SAL-04 strip reads (sales.view). BR-SAL-04-05: the status is READ from here and never copied onto the invoice, so the two cannot disagree.',
    },
    submit: {
        method: 'POST',
        path: '/api/einvoicing/submissions/:submissionId/submit',
        pathParams: submissionParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: SubmissionDetailSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Submit a PENDING record (sales.approve — AP-10). Signs, sends, and appends one log entry (BR-FIN-08-05/-06).',
    },
    resubmit: {
        method: 'POST',
        path: '/api/einvoicing/submissions/:submissionId/resubmit',
        pathParams: submissionParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: SubmissionDetailSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Resubmit a REJECTED record (sales.approve). Recorded as a resubmission because earlier attempts exist (BR-FIN-08-07); a non-rejection clears the old reason and field (BR-FIN-08-08).',
    },
    authorityAnswer: {
        method: 'POST',
        path: '/api/einvoicing/submissions/:submissionId/authority-answer',
        pathParams: submissionParams,
        body: AuthorityAnswerRequestSchema,
        responses: {
            200: SubmissionDetailSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'The authority’s answer arriving AFTER the submitting request returned (BR-FIN-08-14, Q-FIN-08-1). A repeat of the same answer is logged and ignored; a contradiction is refused (§15).',
    },
    config: {
        method: 'GET',
        path: '/api/einvoicing/config',
        responses: {
            200: ComplianceConfigSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The Configuration tab, read side (sales.view): the adapter’s name, format, tax-ID label and required fields, plus the company’s number. Changing any of it is admin.edit through /api/settings.',
    },
    sendToCustomer: {
        method: 'POST',
        path: '/api/sales/invoices/:invoiceId/send',
        pathParams: invoiceParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: SubmissionDetailSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Hand a posted invoice to the customer (sales.edit). Under clearance this is REFUSED until the authority returns Accepted, with BR-SAL-04-01’s own sentence; under reporting it is never blocked (BR-FIN-08-04).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=einvoicing.js.map